import {
  CampaignStatus,
  ClosingMethod,
  Channels,
  CampaignType,
  Campaign
} from 'services/campaign/types';
import {
  CampaignStatus as GeneratedCampaignStatus,
  ClosingMethod as GeneratedClosingMethod,
  Channels as GeneratedChannels,
  CampaignType as GeneratedCampaignType
} from 'generated-graphql/types';
import { GetCampaignsByOrganizationQuery } from 'generated-graphql/hooks/apollo-hooks';
import { User } from 'services/user/types';
import { Agent } from 'services/agents/types';
import { SearchCampaignsQuery } from 'generated-graphql/graphql';

export const mapStatus = (status: GeneratedCampaignStatus): CampaignStatus => {
  const statusMapping: Record<GeneratedCampaignStatus, CampaignStatus> = {
    [GeneratedCampaignStatus.Draft]: CampaignStatus.DRAFT,
    [GeneratedCampaignStatus.Live]: CampaignStatus.LIVE,
    [GeneratedCampaignStatus.GeneratingMessages]:
      CampaignStatus.GENERATING_MESSAGES,
    [GeneratedCampaignStatus.SendingMessages]: CampaignStatus.SENDING_MESSAGES,
    [GeneratedCampaignStatus.Paused]: CampaignStatus.PAUSED,
    [GeneratedCampaignStatus.FinishedSending]: CampaignStatus.FINISHED_SENDING,
    [GeneratedCampaignStatus.FinishedGenerating]:
      CampaignStatus.FINISHED_GENERATING,
    [GeneratedCampaignStatus.Scheduled]: CampaignStatus.SCHEDULED,
    [GeneratedCampaignStatus.Processing]: CampaignStatus.PROCESSING,
    [GeneratedCampaignStatus.Disconnected]: CampaignStatus.DISCONNECTED,
    [GeneratedCampaignStatus.Archived]: CampaignStatus.ARCHIVED
  };
  return statusMapping[status];
};

export const mapClosingMethod = (
  method?: GeneratedClosingMethod | null
): ClosingMethod | null => {
  const closingMethodMapping: Record<GeneratedClosingMethod, ClosingMethod> = {
    [GeneratedClosingMethod.PhoneNumber]: ClosingMethod.PHONE_NUMBER,
    [GeneratedClosingMethod.MeetingUrl]: ClosingMethod.MEETING_URL,
    [GeneratedClosingMethod.Custom]: ClosingMethod.CUSTOM,
    [GeneratedClosingMethod.CalendarMeetingIntegration]:
      ClosingMethod.CALENDAR_MEETING_INTEGRATION
  };
  return method ? closingMethodMapping[method] : null;
};

export const mapChannel = (
  channel?: GeneratedChannels | null
): Channels | undefined | null => {
  const channelMapping: Record<GeneratedChannels, Channels> = {
    [GeneratedChannels.Email]: Channels.EMAIL,
    [GeneratedChannels.Whatsapp]: Channels.WHATSAPP,
    [GeneratedChannels.WebChat]: Channels.WEB_CHAT,
    [GeneratedChannels.Sms]: Channels.SMS,
    [GeneratedChannels.WebChatViaEmail]: Channels.WEB_CHAT_VIA_EMAIL
  };
  return channel ? channelMapping[channel] : null;
};

export const mapType = (
  type?: GeneratedCampaignType | null
): CampaignType | null => {
  const typeMapping: Record<GeneratedCampaignType, CampaignType> = {
    [GeneratedCampaignType.NewCandidateOutreach]:
      CampaignType.NEW_CANDIDATE_OUTREACH,
    [GeneratedCampaignType.ApplicantOutreach]: CampaignType.APPLICANT_OUTREACH,
    [GeneratedCampaignType.EngagementOutreach]:
      CampaignType.ENGAGEMENT_OUTREACH,
    [GeneratedCampaignType.HumanOutreach]: CampaignType.HUMAN_OUTREACH,
    [GeneratedCampaignType.AvailabilityOutreach]:
      CampaignType.AVAILABILITY_OUTREACH,
    [GeneratedCampaignType.RequestCvOutreach]: CampaignType.REQUEST_CV_OUTREACH,
    [GeneratedCampaignType.AtsJobApplicationUrlOutreach]:
      CampaignType.ATS_JOB_APPLICATION_URL_OUTREACH,
    [GeneratedCampaignType.CustomJobApplicationUrlOutreach]:
      CampaignType.CUSTOM_JOB_APPLICATION_URL_OUTREACH,
    [GeneratedCampaignType.Scheduling]: CampaignType.SCHEDULING,
    [GeneratedCampaignType.NotificationOutreach]:
      CampaignType.NOTIFICATION_OUTREACH
  };
  return type ? typeMapping[type] : null;
};

export const mapOrganizationCampaignsToCampaigns = (
  campaignsList: GetCampaignsByOrganizationQuery['getCampaignsByOrganization']
) => {
  if (!campaignsList?.items.length) {
    return [];
  }

  return campaignsList?.items
    .filter((campaign) => campaign !== null)
    .map<Campaign>((item) => {
      return {
        ...item,
        jobDescriptionParsed: {
          ...item?.jobDescriptionParsed,
          location: item?.jobDescriptionParsed?.location || '',
          employerName: item?.jobDescriptionParsed?.employerName || '',
          jobTitle: item?.jobDescriptionParsed?.jobTitle || '',
          summaryOfRole: item?.jobDescriptionParsed?.summaryOfRole || ''
        },
        agentId: item.agentId || '',
        user: item.user ? (item.user as User) : null,
        status: mapStatus(item.status),
        closingMethod: mapClosingMethod(item.closingMethod),
        type: mapType(item.type),
        channel: item.channel,
        questions: [],
        templateId: item.templateId || '',
        totalProspectsCount: item.totalProspectsCount || 0,
        totalConversationsCount: item.totalConversationsCount || 0,
        totalConversationsWithRepliesCount:
          item.totalConversationsWithRepliesCount || 0,
        initialMessagesSentCount: item.initialMessagesSentCount || 0,
        totalMessagesCount: item.totalMessagesCount || 0,
        totalMessagesWithMeetingUrlCount:
          item.totalMessagesWithMeetingUrlCount || 0,
        totalMessagesWithCallRequest: item.totalMessagesWithCallRequest || 0,
        totalMessagesWithCustomRequest:
          item.totalMessagesWithCustomRequest || 0,
        totalNotInterestedConversationCount:
          item.totalNotInterestedConversationCount || 0,
        totalOptedOutConversationCount:
          item.totalOptedOutConversationCount || 0,
        messagesProcessedCount: item.messagesProcessedCount || 0
      };
    });
};

export const mapUserCampaignsToCampaigns = (
  campaignsList: SearchCampaignsQuery['searchCampaigns']
) => {
  if (!campaignsList?.items.length) {
    return [];
  }

  return campaignsList?.items
    .filter((campaign) => campaign !== null)
    .map<Campaign>((item) => {
      return {
        ...item,
        jobDescriptionParsed: {
          ...item?.jobDescriptionParsed,
          location: item?.jobDescriptionParsed?.location || '',
          employerName: item?.jobDescriptionParsed?.employerName || '',
          jobTitle: item?.jobDescriptionParsed?.jobTitle || '',
          summaryOfRole: item?.jobDescriptionParsed?.summaryOfRole || ''
        },
        agentId: item.agentId || '',
        user: item.user ? (item.user as User) : null,
        agent: item.agent ? (item?.agent as Agent) : null,
        status: mapStatus(item.status),
        closingMethod: mapClosingMethod(item.closingMethod),
        type: mapType(item.type),
        channel: item.channel,
        questions: [],
        templateId: item.templateId || '',
        totalProspectsCount: item.totalProspectsCount || 0,
        totalConversationsCount: item.totalConversationsCount || 0,
        totalConversationsWithRepliesCount:
          item.totalConversationsWithRepliesCount || 0,
        initialMessagesSentCount: item.initialMessagesSentCount || 0,
        totalMessagesCount: item.totalMessagesCount || 0,
        totalMessagesWithMeetingUrlCount:
          item.totalMessagesWithMeetingUrlCount || 0,
        totalMessagesWithCallRequest: item.totalMessagesWithCallRequest || 0,
        totalMessagesWithCustomRequest:
          item.totalMessagesWithCustomRequest || 0,
        totalNotInterestedConversationCount:
          item.totalNotInterestedConversationCount || 0,
        totalOptedOutConversationCount:
          item.totalOptedOutConversationCount || 0,
        messagesProcessedCount: item.messagesProcessedCount || 0
      };
    });
};
